/**
 * Attesa della verifica antivirus di un documento caricato (osservazioni n. 4 della Direzione di
 * CIDIS, 23/10/2026, punto 13: «i file si caricano ma non salvano»).
 *
 * Il file e' salvato appena caricato, cifrato nell'archivio. Si scarica dopo la verifica antivirus,
 * che fa il Job dei lavori al suo giro (apps/lavori/src/lavori/antivirus.ts): fino ad allora la
 * linguetta Documenti lo mostra come ricevuto e in verifica, con la previsione calcolata qui. Senza
 * dipendenze dal browser: la prova API la esegue (apps/api/test/osservazioni4-consensi-documenti.test.ts).
 *
 * I giri sono quelli delle due definizioni di Job in infrastruttura/principale.tf, nel fuso di
 * CIDIS: ogni ora dalle 7 alle 19, dal lunedi' al sabato (`cron_lavori_giorno`), e ogni notte alle 4
 * (`cron_lavori_notte`). La prova confronta GIRI_LAVORI con quel file: cambiando il cron senza
 * cambiare qui, la prova si ferma. L'avvio su richiesta, che anticiperebbe il giro, in esercizio non
 * e' attivo (ops/manuali/02-esercizio.md): se si attiva, il file arriva prima di quanto previsto, non dopo.
 */

export const GIRI_LAVORI = {
  fuso: 'Europe/Rome',
  /** `cron_lavori_giorno = "0 7-19 * * 1-6"`: giorni ISO (1 = lunedi'), prima e ultima ora del giro. */
  giorno: { giorni: [1, 2, 3, 4, 5, 6], dalle: 7, alle: 19 },
  /** `cron_lavori_notte = "0 4 * * *"`. */
  notte: { ora: 4 },
} as const

/**
 * Margine oltre il giro previsto prima di dire che la verifica e' in ritardo: il giro dura qualche
 * minuto, e un file che non passa al primo tentativo si riprova al giro dopo (apps/lavori/src/giro.ts).
 */
export const MARGINE_RITARDO_MS = 30 * 60_000

const UN_ORA_MS = 3_600_000

const GIORNI_ISO: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }
const NOMI_GIORNI = ['', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica']

let formattatore: Intl.DateTimeFormat | null = null

/** Giorno, giorno della settimana e ora di un istante sull'orologio di CIDIS. */
function orologio(istante: Date): { giorno: string; giornoSettimana: number; ora: number } {
  // `hourCycle: 'h23'` e non `hour12: false`: con quest'ultimo la mezzanotte esce come «24» in alcune
  // versioni di ICU (stessa scelta di apps/lavori/src/orario.ts).
  formattatore ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: GIRI_LAVORI.fuso, hourCycle: 'h23', weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
  })
  const parti = new Map(formattatore.formatToParts(istante).map((p) => [p.type as string, p.value]))
  return {
    giorno: `${parti.get('year')}-${parti.get('month')}-${parti.get('day')}`,
    giornoSettimana: GIORNI_ISO[parti.get('weekday') ?? ''] ?? 1,
    ora: Number(parti.get('hour')),
  }
}

const eGiro = (istante: Date) => {
  const o = orologio(istante)
  if (o.ora === GIRI_LAVORI.notte.ora) return true
  return (GIRI_LAVORI.giorno.giorni as readonly number[]).includes(o.giornoSettimana)
    && o.ora >= GIRI_LAVORI.giorno.dalle && o.ora <= GIRI_LAVORI.giorno.alle
}

/**
 * Il primo giro dopo l'istante dato. I giri partono all'ora esatta, e a Roma lo scarto dal tempo
 * universale e' sempre di ore intere: basta provare le ore intere che seguono. Il giro gia' partito
 * nell'ora del caricamento non conta, perche' puo' aver svuotato la coda prima della conferma. Il
 * giro notturno c'e' ogni giorno: entro ventiquattro ore se ne trova uno.
 */
export function prossimoGiro(dopo: Date): Date {
  let t = Math.floor(dopo.getTime() / UN_ORA_MS) * UN_ORA_MS + UN_ORA_MS
  for (let i = 0; i < 48 && !eGiro(new Date(t)); i++) t += UN_ORA_MS
  return new Date(t)
}

export type AttesaAntivirus = {
  /** Istante del giro che dovrebbe verificare il file. */
  entro: Date
  /** Il giro e' entro un'ora dal caricamento: in orario di servizio. */
  entroUnOra: boolean
  /** Passato il giro previsto, con il margine, il file e' ancora in verifica. */
  inRitardo: boolean
  /** Quando sara' disponibile, per l'elenco: «entro le 11», «domani dopo le 4», «lunedì dopo le 7». */
  quando: string
  /** Lo stato della verifica in una frase: «È in verifica antivirus: sarà disponibile entro un’ora circa.» */
  verifica: string
  /** La frase da mostrare dopo il caricamento: il file e' salvato, e la verifica. */
  messaggio: string
}

/**
 * Previsione per un documento ricevuto e in attesa della verifica antivirus. `caricatoIl` e' il
 * momento del caricamento (core.documento.caricato_il), `adesso` il momento in cui si guarda: il
 * giorno della previsione si dice rispetto a quando si legge («oggi», «domani»).
 */
export function attesaAntivirus(caricatoIl: Date | string, adesso: Date = new Date()): AttesaAntivirus {
  const caricato = new Date(caricatoIl)
  const entro = prossimoGiro(caricato)
  const entroUnOra = entro.getTime() - caricato.getTime() <= UN_ORA_MS
  const inRitardo = adesso.getTime() > entro.getTime() + MARGINE_RITARDO_MS
  const ora = orologio(entro).ora
  const quando = entroUnOra && orologio(adesso).giorno === orologio(entro).giorno
    ? `entro le ${ora}`
    : `${giornoRelativo(entro, adesso)} dopo le ${ora}${ora < GIRI_LAVORI.giorno.dalle ? ' del mattino' : ''}`
  const verifica = inRitardo
    ? 'La verifica antivirus non è ancora conclusa: riprova più tardi. Se domani non è ancora disponibile, avvisa '
      + 'l’amministratore.'
    : entroUnOra
      ? 'È in verifica antivirus: sarà disponibile entro un’ora circa.'
      : 'È in verifica antivirus, che fuori dall’orario di servizio (dal lunedì al sabato, dalle 7 alle 20) non si fa '
        + `ogni ora: sarà disponibile ${quando}.`
  const messaggio = `Il file è stato ricevuto e salvato. ${verifica}`
  return { entro, entroUnOra, inRitardo, quando, verifica, messaggio }
}

/** Il giorno di calendario dopo AAAA-MM-GG: sul calendario, non aggiungendo 24 ore (ora legale). */
function giornoDopo(giorno: string): string {
  const [a, m, g] = giorno.split('-').map(Number)
  return new Date(Date.UTC(a, m - 1, g + 1)).toISOString().slice(0, 10)
}

/** «oggi», «domani» o il giorno della settimana, sull'orologio di CIDIS. */
function giornoRelativo(giorno: Date, rispetto: Date): string {
  const g = orologio(giorno).giorno
  const oggi = orologio(rispetto).giorno
  if (g === oggi) return 'oggi'
  if (g === giornoDopo(oggi)) return 'domani'
  return NOMI_GIORNI[orologio(giorno).giornoSettimana]
}

/** Etichetta, nell'elenco dei documenti, del file ricevuto e non ancora verificato. */
export const ETICHETTA_IN_VERIFICA = 'In verifica antivirus'
